<script setup lang="ts">
  import { StockItemsService } from '@/api/services/stock-items.service';
  import { StockMovementsService } from '@/api/services/stock-movements.service';
  import { useModal } from '@/composition/use-modal';
  import { useSocket } from '@/composition/use-socket';
  import { useUi } from '@/composition/use-ui';
  import {
    STOCK_MOVEMENT_TYPE_LABELS,
    StockMovementType,
  } from '@/enums/StockMovementType';
  import { AlertType } from '@/interfaces/IAlert';
  import { IStockItem } from '@/interfaces/IStockItem';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { computed, ref } from 'vue';

  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();

  const props = defineProps<{
    item: IStockItem;
  }>();

  const adjustType = ref<'add' | 'subtract' | 'adjust' | null>(null);
  const adjustQuantity = ref<string>('');
  const movementNote = ref<string>('');

  const adjustQuantityTitle = computed(() => {
    if (adjustType.value === 'add') {
      return 'Cantidad a agregar';
    } else if (adjustType.value === 'subtract') {
      return 'Cantidad a restar';
    } else if (adjustType.value === 'adjust') {
      return 'Nuevo stock';
    }
  });

  const movementTypes = computed(() => {
    switch (adjustType.value) {
      case 'add':
        return [
          StockMovementType.PURCHASE_ORDER,
          StockMovementType.STOCK_ADJUSTMENT,
        ];
      case 'subtract':
        return [StockMovementType.STOCK_ADJUSTMENT];
      case 'adjust':
        return [StockMovementType.STOCK_ADJUSTMENT];
    }
  });

  const selectedMovementType = ref<StockMovementType | null>(null);

  const isValid = computed(() => {
    return (
      adjustType.value &&
      selectedMovementType.value &&
      +(adjustQuantity.value || 0) > 0
    );
  });

  const saving = ref(false);
  async function save() {
    if (!isValid.value || saving.value) return;
    if (!restaurantStore.restaurant.id || !restaurantStore.selectedBranch.id) {
      ui.showToast(AlertType.Error, 'No se pudo obtener el restaurante');
      return;
    }

    try {
      saving.value = true;
      const quantity = +(adjustQuantity.value || 0);

      await StockItemsService.adjustStock({
        id: props.item.id,
        quantity: quantity,
        action: adjustType.value,
        type: selectedMovementType.value,
        note: movementNote.value,
      });

      ui.showToast(AlertType.Success, 'Movimiento guardado');
      modals.hide('stock-adjust');
    } catch (error) {
      ui.serverError(error, 'Error al guardar el movimiento de stock');
    } finally {
      saving.value = false;
    }
  }
</script>

<template>
  <v-card class="modal-stock-adjust">
    <v-card-title>
      <span>Stock de {{ item.name }}</span>
    </v-card-title>
    <v-card-text>
      <div class="modal-body">
        <template v-if="!adjustType">
          <div class="adjust-type-buttons">
            <v-btn color="info" @click="adjustType = 'add'">
              <v-icon left>mdi-plus</v-icon>
              <span>Ingreso</span>
            </v-btn>
            <v-btn color="info" @click="adjustType = 'subtract'">
              <v-icon left>mdi-minus</v-icon>
              <span>Salida</span>
            </v-btn>
            <v-btn color="info" @click="adjustType = 'adjust'">
              <v-icon left>mdi-adjust</v-icon>
              <span>Ajuste</span>
            </v-btn>
          </div>
        </template>
        <template v-else>
          <div class="adjust-main">
            <div class="current-stock">
              <span class="current-stock--label">Stock actual</span>
              <span class="current-stock--value"
                >{{ item.quantity }} {{ item.unit }}</span
              >
            </div>
            <div class="adjust-input">
              <span class="adjust-input--label">{{ adjustQuantityTitle }}</span>
              <v-text-field
                v-model="adjustQuantity"
                type="number"
                dense
                placeholder="0"
                autofocus
                autocomplete="off"
              />
            </div>
          </div>
          <div class="movement-type-selector">
            <span class="movement-type-selector--label"
              >Tipo de movimiento</span
            >
            <v-radio-group v-model="selectedMovementType" class="tw-mt-1" dense>
              <v-radio
                v-for="type in movementTypes"
                :key="type"
                :label="STOCK_MOVEMENT_TYPE_LABELS[type].label"
                :value="type"
              />
            </v-radio-group>
          </div>
          <div class="movement-note">
            <span class="movement-note--label">Comentario (opcional)</span>
            <v-text-field
              v-model="movementNote"
              dense
              placeholder="Comentario sobre el movimiento"
              autocomplete="off"
            />
          </div>
        </template>
      </div>
    </v-card-text>
    <v-card-actions>
      <div class="tw-w-full tw-flex tw-justify-center tw-space-x-2">
        <v-btn
          color="grey"
          text
          @click="modals.hide('stock-adjust')"
          :disabled="saving"
        >
          <v-icon left>mdi-close</v-icon>
          Cancelar</v-btn
        >
        <v-btn
          v-if="adjustType"
          color="primary"
          :disabled="!isValid"
          @click="save"
          :loading="saving"
        >
          <v-icon left>mdi-check</v-icon>
          Guardar</v-btn
        >
      </div>
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
  .modal-stock-adjust {
    .modal-body {
      @apply tw-p-0;

      .adjust-type-buttons {
        @apply tw-flex tw-justify-between tw-space-x-2;

        .v-btn {
          @apply tw-flex-1 tw-h-16;
        }
      }

      .adjust-main {
        @apply tw-flex tw-justify-between tw-space-x-2;

        .current-stock {
          @apply tw-flex tw-flex-col tw-space-y-0;

          &--label {
            @apply tw-text-gray-500;
          }

          &--value {
            @apply tw-text-2xl tw-font-bold;
          }
        }

        .adjust-input {
          &--label {
            @apply tw-font-bold tw-text-black;
          }
        }
      }
    }
  }
</style>
